/**
 * Per-field validators for EditableField (Block 6.B.1 / ADR-024 D-validation).
 *
 * Validators return null on pass, an error message string on fail. Per
 * Q7 disposition (round-1 audit 2026-05-07): client validators match
 * server's actual loose acceptance — UX cushion not enforcement. The
 * server is the security boundary; these are nudges that surface
 * obvious typos before the round-trip.
 *
 * Hand-rolled rather than Zod (Q3 disposition) — the validator set is
 * small enough that a schema library doesn't earn its place; Zod lands
 * with audit 7.1's tenants.config schema work in PR-3 / Phase 2.
 */

export type Validator = (value: string) => string | null;

/** Always passes. Default for fields without specific format constraints. */
export const noopValidator: Validator = () => null;

/**
 * Phone — loose: digits + common separators (space / dash / dot / paren).
 * Accepts (555) 555-0123, 555-555-0123, 555.555.0123, 5555550123, etc.
 * Rejects only inputs with NO digits OR explicit gibberish characters.
 */
export const phoneValidator: Validator = (value) => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // Strip allowed separators; what remains must be digits and have at
  // least 7 of them (covers 7-digit local + extension oddities).
  const stripped = trimmed.replace(/[\s().\-+ext]/gi, '');
  if (!/^\d+$/.test(stripped)) {
    return 'Enter the full phone number, with the area code.';
  }
  if (stripped.length < 7) {
    return 'That phone number is too short. Enter all the digits, with the area code.';
  }
  return null;
};

/**
 * Email — loose RFC shape. Won't catch every malformed address but
 * catches obvious typos like missing @ or missing TLD.
 */
export const emailValidator: Validator = (value) => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // local@domain.tld pattern; one @, at least one ., something on each side.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return 'Enter the full email address, with the @.';
  }
  return null;
};

/**
 * URL — must be https:// or http:// prefix, then a host. Loose; accepts
 * trailing path / query / fragment. Empty value ok.
 */
export const urlValidator: Validator = (value) => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^https?:\/\/[^\s]+/i.test(trimmed)) {
    return 'Start the website with https://.';
  }
  return null;
};

/** Required: rejects empty / whitespace-only. */
export const requiredValidator: Validator = (value) => {
  if (value.trim() === '') return 'Fill this in.';
  return null;
};

/**
 * Compose multiple validators left-to-right; first failure wins.
 */
export function composeValidators(...validators: Validator[]): Validator {
  return (value: string) => {
    for (const v of validators) {
      const err = v(value);
      if (err !== null) return err;
    }
    return null;
  };
}
